import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, MapPin, LogOut, LogIn, Clock, WifiOff, Bell, Mail, Battery, AlertTriangle, Fuel, UserRound } from "lucide-react";
import { vehiclesInScope, hasBattery, hasDtc, hasFuel, hasDriver } from "@/lib/capabilities";

const INP = "bg-secondary/60 border border-border rounded-md px-3 py-2 text-sm outline-none focus:border-[#17A2B8]";
const DAYS = [[0, "Lun"], [1, "Mar"], [2, "Mer"], [3, "Jeu"], [4, "Ven"], [5, "Sam"], [6, "Dim"]];
const hh = (n) => String(Number(n)).padStart(2, "0") + ":00";

// Portée client -> scope backend (RBAC serveur = autorité ; scope_type/id jamais montré au client).
export function scopeToBackend({ kind, id }, isAdmin, companies) {
  if (kind === "fleet") return isAdmin ? { scope_type: "global", scope_id: null } : { scope_type: "company", scope_id: companies[0]?.id || null };
  return { scope_type: kind, scope_id: id };
}
const skFromScope = (st) => (st === "global" ? "fleet" : st === "company" ? "company" : st === "pool" ? "pool" : st === "vehicle" ? "vehicle" : "advanced");

// ---- Détection : une règle A9.1 est-elle une fiche express (zone / hors horaires) ? ----
export function classifyA9(rule) {
  const groups = (rule.groups && rule.groups.length) ? rule.groups : (rule.conditions?.length ? [{ op: "AND", conditions: rule.conditions }] : []);
  const all = groups.flatMap((g) => g.conditions || []);
  const metrics = all.map((c) => c.metric);
  // Zone : une seule condition zone, un seul groupe AND, uniquement zone/weekday/time_window
  if (metrics.includes("zone")) {
    const zoneConds = all.filter((c) => c.metric === "zone");
    const onlyAllowed = all.every((c) => ["zone", "weekday", "time_window"].includes(c.metric));
    if (onlyAllowed && zoneConds.length === 1 && groups.length === 1 && (groups[0].op || "AND").toUpperCase() !== "OR") {
      const zc = zoneConds[0];
      const wd = all.find((c) => c.metric === "weekday");
      const tw = all.find((c) => c.metric === "time_window");
      return {
        type: "zone", mode: zc.op === "inside" ? "entree" : "sortie", zone_id: zc.value,
        when: tw ? "hours" : "always",
        days: wd ? (wd.value || []).map(Number) : [0, 1, 2, 3, 4, 5, 6],
        h0: tw ? parseInt(tw.value.start, 10) : 7, h1: tw ? parseInt(tw.value.end, 10) : 18,
        scopeKind: skFromScope(rule.scope_type), scopeId: rule.scope_id, raw: rule,
      };
    }
    return { type: "advanced", raw: rule };
  }
  // Hors horaires : G1 = AND[moving==true] (nouveau) ou AND[ignition==on] (ancien, rétrocompat) ;
  // G2 = OR[ (weekday not_in) ?, time_window in ]
  if (groups.length === 2) {
    const [g1, g2] = groups;
    const g1c = g1.conditions || [];
    const g1ok = g1c.length === 1 && (
      (g1c[0].metric === "moving" && ["true", "on", "1"].includes(String(g1c[0].value).toLowerCase())) ||
      (g1c[0].metric === "ignition" && String(g1c[0].value) === "on")
    );
    const g2c = g2.conditions || [];
    const g2ok = (g2.op || "").toUpperCase() === "OR" && g2c.some((c) => c.metric === "time_window") && g2c.every((c) => ["weekday", "time_window"].includes(c.metric));
    if (g1ok && g2ok) {
      const tw = g2c.find((c) => c.metric === "time_window");
      const wd = g2c.find((c) => c.metric === "weekday");
      // plage INTERDITE start=fin autorisée(=hour_end), end=début autorisé(=hour_start)
      return {
        type: "horaires", h0: tw ? parseInt(tw.value.end, 10) : 7, h1: tw ? parseInt(tw.value.start, 10) : 18,
        days: wd ? (wd.value || []).map(Number) : [0, 1, 2, 3, 4, 5, 6],
        scopeKind: skFromScope(rule.scope_type), scopeId: rule.scope_id, raw: rule,
      };
    }
  }
  return { type: "advanced", raw: rule };
}

// ---- Bloc portée réutilisable (jamais de scope_type/scope_id affiché) ----
function ScopeField({ scopeKind, setScopeKind, scopeId, setScopeId, companies, pools, vehicles, fleetAvailable, disabled }) {
  return (
    <div>
      <p className="label-mono mb-1.5">Pour</p>
      <div className="flex gap-2 flex-wrap">
        <select value={scopeKind} onChange={(e) => { setScopeKind(e.target.value); setScopeId(""); }} data-testid="express-scope-kind" className={INP} disabled={disabled}>
          {fleetAvailable && <option value="fleet">Toute ma flotte</option>}
          <option value="company">Une société</option>
          <option value="pool">Un groupe</option>
          <option value="vehicle">Un véhicule</option>
        </select>
        {scopeKind === "company" && <select value={scopeId} onChange={(e) => setScopeId(e.target.value)} data-testid="express-scope-entity" className={INP} disabled={disabled}><option value="">— Société —</option>{companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select>}
        {scopeKind === "pool" && <select value={scopeId} onChange={(e) => setScopeId(e.target.value)} data-testid="express-scope-entity" className={INP} disabled={disabled}><option value="">— Groupe —</option>{pools.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select>}
        {scopeKind === "vehicle" && <select value={scopeId} onChange={(e) => setScopeId(e.target.value)} data-testid="express-scope-entity" className={INP} disabled={disabled}><option value="">— Véhicule —</option>{vehicles.map((v) => <option key={v.id} value={v.id}>{v.name} · {v.plate}</option>)}</select>}
      </div>
    </div>
  );
}

// Notifications (A6) : la config A6 existe uniquement aux portées global/société et pour un
// sous-ensemble de types (via l'onglet « Notifications »). Elle ne couvre PAS les portées
// pool/véhicule ni le DTC, et regroupe zone/horaires sous « composée ». Pour ne rien
// promettre de faux, on n'expose PAS de bascule ici : on renvoie vers l'onglet dédié.
function NotifyPlaceholder() {
  return (
    <div className="rounded-lg bg-secondary/40 border border-border px-3 py-2.5" data-testid="notify-placeholder">
      <p className="label-mono mb-1 flex items-center gap-1.5"><Bell className="w-3.5 h-3.5" /> Notifications</p>
      <p className="text-xs text-muted-foreground flex items-center gap-1.5">
        <Mail className="w-3.5 h-3.5 shrink-0" /> Activez les notifications in-app ou e-mail depuis l'onglet <span className="font-semibold text-foreground">« Notifications »</span> (aucun canal n'est actif tant qu'aucune règle n'y est configurée).
      </p>
    </div>
  );
}

function Shell({ title, icon: Icon, color, onClose, children, footer }) {
  return (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-3 sm:p-6" onClick={onClose}>
      <div className="bg-card border border-border rounded-2xl w-full max-w-lg my-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h2 className="font-extrabold text-lg flex items-center gap-2"><Icon className="w-5 h-5" style={{ color }} /> {title}</h2>
          <button onClick={onClose} data-testid="express-close" className="text-muted-foreground hover:text-foreground p-1"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-5">{children}</div>
        <div className="px-5 py-4 border-t border-border flex justify-end">{footer}</div>
      </div>
    </div>
  );
}

function useScope(editing, isAdmin) {
  const [scopeKind, setScopeKind] = useState(isAdmin ? "fleet" : "company");
  const [scopeId, setScopeId] = useState("");
  useEffect(() => {
    if (editing) { setScopeKind(editing.scopeKind || "company"); setScopeId(editing.scopeId || ""); }
    else { setScopeKind(isAdmin ? "fleet" : "company"); setScopeId(""); }
  }, [editing, isAdmin]);
  return { scopeKind, setScopeKind, scopeId, setScopeId };
}

// ============================ ZONE (entrée / sortie) ============================
export function ZoneExpress({ open, onClose, onSaved, mode, zones, companies, pools, vehicles, isAdmin, editing }) {
  const fleetAvailable = isAdmin || companies.length === 1;
  const eff = editing || {};
  const [zoneId, setZoneId] = useState("");
  const [when, setWhen] = useState("always");
  const [days, setDays] = useState([0, 1, 2, 3, 4]);
  const [h0, setH0] = useState(7);
  const [h1, setH1] = useState(18);
  const [saving, setSaving] = useState(false);
  const { scopeKind, setScopeKind, scopeId, setScopeId } = useScope(editing, isAdmin);
  const m = editing ? editing.mode : mode; // entree | sortie
  const isEntree = m === "entree";

  useEffect(() => {
    if (!open) return;
    if (editing) { setZoneId(eff.zone_id || ""); setWhen(eff.when || "always"); setDays(eff.days || [0, 1, 2, 3, 4]); setH0(eff.h0 ?? 7); setH1(eff.h1 ?? 18); }
    else { setZoneId(""); setWhen("always"); setDays([0, 1, 2, 3, 4]); setH0(7); setH1(18); }
  }, [open, editing]); // eslint-disable-line

  if (!open) return null;
  const zoneName = zones.find((z) => z.id === zoneId)?.name || "?";
  const scopeChosen = scopeKind === "fleet" || !!scopeId;
  const ready = !!zoneId && scopeChosen;

  const save = async () => {
    setSaving(true);
    try {
      const { scope_type, scope_id } = scopeToBackend({ kind: scopeKind, id: scopeId }, isAdmin, companies);
      const conditions = [{ metric: "zone", op: isEntree ? "inside" : "outside", value: zoneId }];
      if (when === "hours") {
        if (days.length && days.length < 7) conditions.push({ metric: "weekday", op: "in", value: days.map(String) });
        conditions.push({ metric: "time_window", op: "in", value: { start: hh(h0), end: hh(h1) } });
      }
      const body = {
        name: (editing && editing.raw && editing.raw.name) || `${isEntree ? "Entrée" : "Sortie"} de zone ${zoneName}`.trim(),
        severity: (editing && editing.raw && editing.raw.severity) || "medium",
        enabled: editing ? (editing.raw.enabled !== false) : true,
        scope_type, scope_id, duration_s: 0, groups: [{ op: "AND", conditions }],
      };
      if (editing) await api.put(`/alert-rules/${editing.raw.id}`, body);
      else await api.post("/alert-rules", body);
      toast.success(editing ? "Alerte mise à jour" : "Alerte créée");
      onSaved(); onClose();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((typeof d === "string" ? d : d?.message || d?.error) || "Erreur");
    } finally { setSaving(false); }
  };

  return (
    <Shell title={isEntree ? "Entrée de zone" : "Sortie de zone"} icon={isEntree ? LogIn : LogOut} color="#17A2B8" onClose={onClose}
      footer={<button onClick={save} disabled={saving || !ready} data-testid="express-create" className="bg-[#17A2B8] hover:bg-[#1391a5] disabled:opacity-40 text-white font-bold px-6 py-2.5 rounded-md text-sm">{editing ? "Enregistrer" : "Créer l'alerte"}</button>}>
      <div>
        <p className="text-base font-bold mb-2">Quelle zone souhaitez-vous surveiller ?</p>
        <select value={zoneId} autoFocus onChange={(e) => setZoneId(e.target.value)} data-testid="express-zone" className={`${INP} w-full text-base`}>
          <option value="">— Choisir une zone —</option>
          {zones.map((z) => <option key={z.id} value={z.id}>{z.name}</option>)}
        </select>
        {zones.length === 0 && <p className="text-xs text-[#F5821F] mt-1">Aucune zone définie. Créez d'abord une zone dans la carte.</p>}
      </div>
      <ScopeField {...{ scopeKind, setScopeKind, scopeId, setScopeId, companies, pools, vehicles, fleetAvailable }} disabled={!!editing} />
      <div>
        <p className="label-mono mb-1.5">Quand</p>
        <select value={when} onChange={(e) => setWhen(e.target.value)} data-testid="express-when" className={INP}>
          <option value="always">Tout le temps</option>
          <option value="hours">Horaires personnalisés</option>
        </select>
        {when === "hours" && (
          <div className="mt-2 space-y-2 pl-1">
            <div className="flex gap-1 flex-wrap">{DAYS.map(([d, l]) => { const on = days.includes(d); return <button key={d} type="button" data-testid={`express-day-${d}`} onClick={() => setDays((s) => s.includes(d) ? s.filter((x) => x !== d) : [...s, d].sort())} className={`text-xs w-11 h-8 rounded border ${on ? "bg-[#17A2B8] text-white border-[#17A2B8]" : "border-border text-muted-foreground"}`}>{l}</button>; })}</div>
            <div className="flex items-center gap-2"><input type="number" min="0" max="24" value={h0} onChange={(e) => setH0(e.target.value)} data-testid="express-h0" className={`${INP} w-20`} /><span className="text-sm">h →</span><input type="number" min="0" max="24" value={h1} onChange={(e) => setH1(e.target.value)} data-testid="express-h1" className={`${INP} w-20`} /><span className="text-sm">h</span></div>
          </div>
        )}
      </div>
      <NotifyPlaceholder />
      <p className="text-sm text-muted-foreground bg-[#17A2B8]/5 border border-[#17A2B8]/20 rounded-lg px-3 py-2" data-testid="express-summary">
        Vous serez alerté lorsqu'un véhicule {isEntree ? "entrera dans" : "sortira de"} la zone {zoneId ? `« ${zoneName} »` : "sélectionnée"}.
      </p>
    </Shell>
  );
}

// ============================ HORS HORAIRES ============================
export function HorairesExpress({ open, onClose, onSaved, companies, pools, vehicles, isAdmin, editing }) {
  const fleetAvailable = isAdmin || companies.length === 1;
  const eff = editing || {};
  const [days, setDays] = useState([0, 1, 2, 3, 4]);
  const [h0, setH0] = useState(7);
  const [h1, setH1] = useState(18);
  const [saving, setSaving] = useState(false);
  const { scopeKind, setScopeKind, scopeId, setScopeId } = useScope(editing, isAdmin);

  useEffect(() => {
    if (!open) return;
    if (editing) { setDays(eff.days || [0, 1, 2, 3, 4]); setH0(eff.h0 ?? 7); setH1(eff.h1 ?? 18); }
    else { setDays([0, 1, 2, 3, 4]); setH0(7); setH1(18); }
  }, [open, editing]); // eslint-disable-line

  if (!open) return null;
  const scopeChosen = scopeKind === "fleet" || !!scopeId;
  const ready = days.length > 0 && scopeChosen;
  const FULL = ["lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi", "dimanche"];
  const ds = [...days].sort((a, b) => a - b);
  const daysTxt = ds.length === 7 ? "tous les jours" : ds.length === 0 ? "" : ds.every((v, i) => i === 0 || v === ds[i - 1] + 1) ? `du ${FULL[ds[0]]} au ${FULL[ds[ds.length - 1]]}` : ds.map((d) => FULL[d]).join(", ");

  const save = async () => {
    setSaving(true);
    try {
      const { scope_type, scope_id } = scopeToBackend({ kind: scopeKind, id: scopeId }, isAdmin, companies);
      const s = hh(h1); // fin autorisée
      const e = hh(h0); // début autorisé
      // Déplacement confirmé (anti-dérive, remorquage contact coupé inclus) ET ( jour NON autorisé OU heure hors plage ).
      const g1 = { op: "AND", conditions: [{ metric: "moving", op: "==", value: "true" }] };
      const orConds = [];
      if (days.length && days.length < 7) orConds.push({ metric: "weekday", op: "not_in", value: days.map(String) });
      orConds.push({ metric: "time_window", op: "in", value: { start: s, end: e } });
      const body = {
        name: (editing && editing.raw && editing.raw.name) || `Utilisation hors ${hh(h0).slice(0, 2)}h-${hh(h1).slice(0, 2)}h`,
        severity: (editing && editing.raw && editing.raw.severity) || "medium",
        enabled: editing ? (editing.raw.enabled !== false) : true,
        scope_type, scope_id, duration_s: 0, groups: [g1, { op: "OR", conditions: orConds }],
      };
      if (editing) await api.put(`/alert-rules/${editing.raw.id}`, body);
      else await api.post("/alert-rules", body);
      toast.success(editing ? "Alerte mise à jour" : "Alerte créée");
      onSaved(); onClose();
    } catch (e2) {
      const d = e2?.response?.data?.detail;
      toast.error((typeof d === "string" ? d : d?.message || d?.error) || "Erreur");
    } finally { setSaving(false); }
  };

  return (
    <Shell title="Utilisation hors horaires" icon={Clock} color="#F5821F" onClose={onClose}
      footer={<button onClick={save} disabled={saving || !ready} data-testid="express-create" className="bg-[#17A2B8] hover:bg-[#1391a5] disabled:opacity-40 text-white font-bold px-6 py-2.5 rounded-md text-sm">{editing ? "Enregistrer" : "Créer l'alerte"}</button>}>
      <div>
        <p className="text-base font-bold mb-2">Autoriser l'utilisation uniquement pendant :</p>
        <div className="flex gap-1 flex-wrap mb-3" data-testid="express-days">{DAYS.map(([d, l]) => { const on = days.includes(d); return <button key={d} type="button" data-testid={`express-day-${d}`} onClick={() => setDays((s) => s.includes(d) ? s.filter((x) => x !== d) : [...s, d].sort())} className={`text-sm w-12 h-9 rounded border font-semibold ${on ? "bg-[#17A2B8] text-white border-[#17A2B8]" : "border-border text-muted-foreground"}`}>{l}</button>; })}</div>
        <div className="flex items-center gap-2">
          <span className="text-sm">De</span>
          <input type="number" min="0" max="24" value={h0} onChange={(e) => setH0(e.target.value)} data-testid="express-h0" className={`${INP} w-20 text-lg font-bold text-center`} />
          <span className="text-sm">h  à</span>
          <input type="number" min="0" max="24" value={h1} onChange={(e) => setH1(e.target.value)} data-testid="express-h1" className={`${INP} w-20 text-lg font-bold text-center`} />
          <span className="text-sm">h</span>
        </div>
      </div>
      <ScopeField {...{ scopeKind, setScopeKind, scopeId, setScopeId, companies, pools, vehicles, fleetAvailable }} disabled={!!editing} />
      <NotifyPlaceholder />
      <p className="text-sm text-muted-foreground bg-[#F5821F]/5 border border-[#F5821F]/20 rounded-lg px-3 py-2" data-testid="express-summary">
        Vous serez alerté en cas de déplacement d'un véhicule en dehors des horaires autorisés ({daysTxt ? daysTxt + ", " : ""}{hh(h0).slice(0, 2)}h00–{hh(h1).slice(0, 2)}h00).
      </p>
    </Shell>
  );
}

// ============================ TRACEUR HORS LIGNE (A4 deconnexion) ============================
export function DeconnexionExpress({ open, onClose, onSaved, companies, pools, vehicles, isAdmin, editing }) {
  const fleetAvailable = isAdmin || companies.length === 1;
  const [minutes, setMinutes] = useState(15);
  const [saving, setSaving] = useState(false);
  const { scopeKind, setScopeKind, scopeId, setScopeId } = useScope(editing, isAdmin);

  useEffect(() => {
    if (!open) return;
    if (editing) setMinutes(editing.minutes ?? 15);
    else setMinutes(15);
  }, [open, editing]); // eslint-disable-line

  if (!open) return null;
  const scopeChosen = scopeKind === "fleet" || !!scopeId;
  const mn = Number(minutes);
  const valid = mn >= 1 && mn <= 1440;
  const ready = valid && scopeChosen;

  const save = async () => {
    setSaving(true);
    try {
      const { scope_type, scope_id } = scopeToBackend({ kind: scopeKind, id: scopeId }, isAdmin, companies);
      const body = { active: true, always_on: true, params: { offline_after_s: Math.round(mn * 60) } };
      const qs = `scope_type=${scope_type}` + (scope_id ? `&scope_id=${encodeURIComponent(scope_id)}` : "");
      await api.put(`/alert-settings/deconnexion?${qs}`, body);
      toast.success(editing ? "Alerte mise à jour" : "Alerte créée");
      onSaved(); onClose();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((typeof d === "string" ? d : d?.message || d?.error) || "Erreur");
    } finally { setSaving(false); }
  };

  return (
    <Shell title="Traceur hors ligne" icon={WifiOff} color="#EF4444" onClose={onClose}
      footer={<button onClick={save} disabled={saving || !ready} data-testid="express-create" className="bg-[#17A2B8] hover:bg-[#1391a5] disabled:opacity-40 text-white font-bold px-6 py-2.5 rounded-md text-sm">{editing ? "Enregistrer" : "Créer l'alerte"}</button>}>
      <div>
        <p className="text-base font-bold mb-2">M'alerter si le traceur ne communique plus depuis</p>
        <div className="flex items-end gap-3">
          <input type="number" min="1" max="1440" value={minutes} autoFocus data-testid="express-minutes" onChange={(e) => setMinutes(e.target.value)}
            className="bg-secondary/60 border-2 border-[#17A2B8]/40 focus:border-[#17A2B8] rounded-xl px-4 py-3 text-4xl font-extrabold text-center w-36 outline-none" />
          <span className="text-xl font-bold pb-3">minutes</span>
        </div>
        {!valid && <p className="text-xs text-[#EF4444] mt-1">Valeur autorisée : 1 à 1440 minutes.</p>}
      </div>
      <ScopeField {...{ scopeKind, setScopeKind, scopeId, setScopeId, companies, pools, vehicles, fleetAvailable }} disabled={!!editing} />
      <NotifyPlaceholder />
      <p className="text-sm text-muted-foreground bg-[#EF4444]/5 border border-[#EF4444]/20 rounded-lg px-3 py-2" data-testid="express-summary">
        Vous serez alerté si un traceur ne communique plus pendant {mn || 15} minutes.
      </p>
    </Shell>
  );
}

export { MapPin };

// Bandeau « périmètre partiellement équipé » (§4) — calculé depuis la liste déjà chargée.
function CompatNote({ vehicles, pools, scopeKind, scopeId, pred, noun }) {
  const scoped = vehiclesInScope(vehicles || [], scopeKind, scopeId, pools);
  if (!scoped.length) return null;
  const compat = scoped.filter(pred).length;
  const partial = compat < scoped.length;
  return (
    <p className={`text-xs rounded-lg px-3 py-2 ${compat === 0 ? "bg-[#EF4444]/10 border border-[#EF4444]/30 text-[#EF4444]" : "bg-secondary/50 border border-border text-muted-foreground"}`} data-testid="express-compat">
      {compat === 0
        ? `Aucun véhicule ${noun} dans ce périmètre — l'alerte ne pourra pas s'appliquer.`
        : partial
          ? `Disponible sur ${compat} véhicule${compat > 1 ? "s" : ""} sur ${scoped.length}. Cette alerte s'appliquera uniquement aux véhicules ${noun}.`
          : `${compat} véhicule${compat > 1 ? "s" : ""} ${noun} dans ce périmètre.`}
    </p>
  );
}

// ============================ BATTERIE FAIBLE (A4 batterie) ============================
export function BatterieExpress({ open, onClose, onSaved, companies, pools, vehicles, isAdmin, editing }) {
  const fleetAvailable = isAdmin || companies.length === 1;
  const [openV, setOpenV] = useState(11.0);
  const [closeV, setCloseV] = useState(11.5);
  const [saving, setSaving] = useState(false);
  const { scopeKind, setScopeKind, scopeId, setScopeId } = useScope(editing, isAdmin);

  useEffect(() => {
    if (!open) return;
    if (editing) { setOpenV(editing.openV ?? 11.0); setCloseV(editing.closeV ?? 11.5); }
    else { setOpenV(11.0); setCloseV(11.5); }
  }, [open, editing]); // eslint-disable-line

  if (!open) return null;
  const scopeChosen = scopeKind === "fleet" || !!scopeId;
  const ov = Number(openV);
  const valid = ov >= 6 && ov <= 36;
  const compat = vehiclesInScope(vehicles || [], scopeKind, scopeId, pools).filter(hasBattery).length;
  const ready = valid && scopeChosen && compat > 0;

  const save = async () => {
    setSaving(true);
    try {
      const { scope_type, scope_id } = scopeToBackend({ kind: scopeKind, id: scopeId }, isAdmin, companies);
      const body = { active: true, always_on: true, params: { open_below_mv: Math.round(ov * 1000), close_above_mv: Math.round(Number(closeV || ov) * 1000) } };
      const qs = `scope_type=${scope_type}` + (scope_id ? `&scope_id=${encodeURIComponent(scope_id)}` : "");
      await api.put(`/alert-settings/batterie?${qs}`, body);
      toast.success(editing ? "Alerte mise à jour" : "Alerte créée");
      onSaved(); onClose();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((typeof d === "string" ? d : d?.message || d?.error) || "Erreur");
    } finally { setSaving(false); }
  };

  return (
    <Shell title="Batterie faible" icon={Battery} color="#F5821F" onClose={onClose}
      footer={<button onClick={save} disabled={saving || !ready} data-testid="express-create" className="bg-[#17A2B8] hover:bg-[#1391a5] disabled:opacity-40 text-white font-bold px-6 py-2.5 rounded-md text-sm">{editing ? "Enregistrer" : "Créer l'alerte"}</button>}>
      <div>
        <p className="text-base font-bold mb-2">M'alerter lorsque la tension descend sous</p>
        <div className="flex items-end gap-3">
          <input type="number" step="0.1" min="6" max="36" value={openV} autoFocus data-testid="express-volts" onChange={(e) => setOpenV(e.target.value)}
            className="bg-secondary/60 border-2 border-[#17A2B8]/40 focus:border-[#17A2B8] rounded-xl px-4 py-3 text-4xl font-extrabold text-center w-36 outline-none" />
          <span className="text-xl font-bold pb-3">V</span>
        </div>
        {!valid && <p className="text-xs text-[#EF4444] mt-1">Valeur autorisée : 6 à 36 V.</p>}
        <details className="text-sm mt-2">
          <summary className="cursor-pointer text-muted-foreground text-xs" data-testid="express-battery-advanced">Option avancée</summary>
          <div className="flex items-end gap-2 mt-2">
            <span className="text-xs text-muted-foreground pb-2">Considérer la batterie rétablie à</span>
            <input type="number" step="0.1" min="6" max="36" value={closeV} data-testid="express-volts-close" onChange={(e) => setCloseV(e.target.value)} className={`${INP} w-24`} />
            <span className="text-xs pb-2">V</span>
          </div>
        </details>
      </div>
      <ScopeField {...{ scopeKind, setScopeKind, scopeId, setScopeId, companies, pools, vehicles, fleetAvailable }} disabled={!!editing} />
      <CompatNote vehicles={vehicles} pools={pools} scopeKind={scopeKind} scopeId={scopeId} pred={hasBattery} noun="équipés d'une mesure de tension" />
      <div><p className="label-mono mb-1.5">Quand</p><div className={`${INP} inline-block text-muted-foreground`}>Tout le temps</div></div>
      <NotifyPlaceholder />
      <p className="text-sm text-muted-foreground bg-[#F5821F]/5 border border-[#F5821F]/20 rounded-lg px-3 py-2" data-testid="express-summary">
        Vous serez alerté lorsque la tension batterie descendra sous {ov || 11} V.
      </p>
    </Shell>
  );
}

// ============================ DÉFAUT MOTEUR / DTC (A4 dtc, on/off) ============================
export function DtcExpress({ open, onClose, onSaved, companies, pools, vehicles, isAdmin, editing }) {
  const fleetAvailable = isAdmin || companies.length === 1;
  const [saving, setSaving] = useState(false);
  const { scopeKind, setScopeKind, scopeId, setScopeId } = useScope(editing, isAdmin);

  if (!open) return null;
  const scopeChosen = scopeKind === "fleet" || !!scopeId;
  const compat = vehiclesInScope(vehicles || [], scopeKind, scopeId, pools).filter(hasDtc).length;
  const ready = scopeChosen && compat > 0;

  const save = async () => {
    setSaving(true);
    try {
      const { scope_type, scope_id } = scopeToBackend({ kind: scopeKind, id: scopeId }, isAdmin, companies);
      const body = { active: true, always_on: true };
      const qs = `scope_type=${scope_type}` + (scope_id ? `&scope_id=${encodeURIComponent(scope_id)}` : "");
      await api.put(`/alert-settings/dtc?${qs}`, body);
      toast.success(editing ? "Alerte mise à jour" : "Alerte créée");
      onSaved(); onClose();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((typeof d === "string" ? d : d?.message || d?.error) || "Erreur");
    } finally { setSaving(false); }
  };

  return (
    <Shell title="Défaut moteur" icon={AlertTriangle} color="#EF4444" onClose={onClose}
      footer={<button onClick={save} disabled={saving || !ready} data-testid="express-create" className="bg-[#17A2B8] hover:bg-[#1391a5] disabled:opacity-40 text-white font-bold px-6 py-2.5 rounded-md text-sm">{editing ? "Enregistrer" : "Créer l'alerte"}</button>}>
      <p className="text-base font-bold" data-testid="express-dtc-desc">Être alerté lorsqu'un défaut moteur (DTC) est détecté sur le bus CAN du véhicule.</p>
      <ScopeField {...{ scopeKind, setScopeKind, scopeId, setScopeId, companies, pools, vehicles, fleetAvailable }} disabled={!!editing} />
      <CompatNote vehicles={vehicles} pools={pools} scopeKind={scopeKind} scopeId={scopeId} pred={hasDtc} noun="équipés d'un diagnostic moteur" />
      <NotifyPlaceholder />
      <p className="text-sm text-muted-foreground bg-[#EF4444]/5 border border-[#EF4444]/20 rounded-lg px-3 py-2" data-testid="express-summary">
        Vous serez alerté dès qu'un défaut moteur sera détecté sur les véhicules équipés.
      </p>
    </Shell>
  );
}

// ============================ CARBURANT (bas / baisse anormale) — A4 ============================
export function CarburantExpress({ open, onClose, onSaved, companies, pools, vehicles, isAdmin, editing }) {
  const fleetAvailable = isAdmin || companies.length === 1;
  const [mode, setMode] = useState("bas"); // bas | baisse
  const [lowPct, setLowPct] = useState(15);
  const [dropPct, setDropPct] = useState(10);
  const [dropWin, setDropWin] = useState(30);
  const [saving, setSaving] = useState(false);
  const { scopeKind, setScopeKind, scopeId, setScopeId } = useScope(editing, isAdmin);

  useEffect(() => {
    if (!open) return;
    if (editing) { setMode(editing.mode || "bas"); setLowPct(editing.lowPct ?? 15); setDropPct(editing.dropPct ?? 10); setDropWin(editing.dropWin ?? 30); }
    else { setMode("bas"); setLowPct(15); setDropPct(10); setDropWin(30); }
  }, [open, editing]); // eslint-disable-line

  if (!open) return null;
  const scopeChosen = scopeKind === "fleet" || !!scopeId;
  const compat = vehiclesInScope(vehicles || [], scopeKind, scopeId, pools).filter(hasFuel).length;
  const low = Number(lowPct), dp = Number(dropPct), dw = Number(dropWin);
  const valid = mode === "bas" ? (low >= 1 && low <= 99) : (dp >= 1 && dp <= 100 && dw >= 1 && dw <= 720);
  const ready = valid && scopeChosen && compat > 0;

  const save = async () => {
    setSaving(true);
    try {
      const { scope_type, scope_id } = scopeToBackend({ kind: scopeKind, id: scopeId }, isAdmin, companies);
      const qs = `scope_type=${scope_type}` + (scope_id ? `&scope_id=${encodeURIComponent(scope_id)}` : "");
      if (mode === "bas") {
        const rec = Math.min(100, low + 5);
        await api.put(`/alert-settings/carburant_bas?${qs}`, { active: true, always_on: true, params: { fuel_low_pct: Math.round(low), fuel_recover_pct: Math.round(rec) } });
      } else {
        await api.put(`/alert-settings/baisse_carburant?${qs}`, { active: true, always_on: true, params: { fuel_drop_pct: Math.round(dp), fuel_drop_window_min: Math.round(dw) } });
      }
      toast.success(editing ? "Alerte mise à jour" : "Alerte créée");
      onSaved(); onClose();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((typeof d === "string" ? d : d?.message || d?.error) || "Erreur");
    } finally { setSaving(false); }
  };

  return (
    <Shell title="Carburant" icon={Fuel} color="#17A2B8" onClose={onClose}
      footer={<button onClick={save} disabled={saving || !ready} data-testid="express-create" className="bg-[#17A2B8] hover:bg-[#1391a5] disabled:opacity-40 text-white font-bold px-6 py-2.5 rounded-md text-sm">{editing ? "Enregistrer" : "Créer l'alerte"}</button>}>
      <div className="flex gap-2" data-testid="fuel-mode">
        <button type="button" onClick={() => setMode("bas")} data-testid="fuel-mode-bas" disabled={!!editing} className={`flex-1 border rounded-lg px-3 py-2 text-sm font-semibold ${mode === "bas" ? "border-[#17A2B8] bg-[#17A2B8]/10 text-[#17A2B8]" : "border-border text-muted-foreground"}`}>Niveau bas</button>
        <button type="button" onClick={() => setMode("baisse")} data-testid="fuel-mode-baisse" disabled={!!editing} className={`flex-1 border rounded-lg px-3 py-2 text-sm font-semibold ${mode === "baisse" ? "border-[#17A2B8] bg-[#17A2B8]/10 text-[#17A2B8]" : "border-border text-muted-foreground"}`}>Baisse anormale</button>
      </div>
      {mode === "bas" ? (
        <div>
          <p className="text-base font-bold mb-2">M'alerter lorsque le niveau descend sous</p>
          <div className="flex items-end gap-3">
            <input type="number" min="1" max="99" value={lowPct} autoFocus data-testid="express-fuel-low" onChange={(e) => setLowPct(e.target.value)}
              className="bg-secondary/60 border-2 border-[#17A2B8]/40 focus:border-[#17A2B8] rounded-xl px-4 py-3 text-4xl font-extrabold text-center w-32 outline-none" />
            <span className="text-xl font-bold pb-3">%</span>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-base font-bold">M'alerter en cas de baisse supérieure à</p>
          <div className="flex items-center gap-2">
            <input type="number" min="1" max="100" value={dropPct} data-testid="express-fuel-drop" onChange={(e) => setDropPct(e.target.value)} className={`${INP} w-24 text-lg font-bold text-center`} />
            <span className="text-sm">% sur environ</span>
            <input type="number" min="1" max="720" value={dropWin} data-testid="express-fuel-window" onChange={(e) => setDropWin(e.target.value)} className={`${INP} w-24 text-lg font-bold text-center`} />
            <span className="text-sm">minutes</span>
          </div>
        </div>
      )}
      <ScopeField {...{ scopeKind, setScopeKind, scopeId, setScopeId, companies, pools, vehicles, fleetAvailable }} disabled={!!editing} />
      <CompatNote vehicles={vehicles} pools={pools} scopeKind={scopeKind} scopeId={scopeId} pred={hasFuel} noun="équipés d'un capteur carburant" />
      <NotifyPlaceholder />
    </Shell>
  );
}

// ============================ CONDUCTEUR NON IDENTIFIÉ — A4 ============================
export function ConducteurExpress({ open, onClose, onSaved, companies, pools, vehicles, isAdmin, editing }) {
  const fleetAvailable = isAdmin || companies.length === 1;
  const [minutes, setMinutes] = useState(1);
  const [saving, setSaving] = useState(false);
  const { scopeKind, setScopeKind, scopeId, setScopeId } = useScope(editing, isAdmin);

  useEffect(() => {
    if (!open) return;
    setMinutes(editing ? (editing.minutes ?? 1) : 1);
  }, [open, editing]); // eslint-disable-line

  if (!open) return null;
  const scopeChosen = scopeKind === "fleet" || !!scopeId;
  const mn = Number(minutes);
  const valid = mn * 60 >= 10 && mn * 60 <= 3600;
  const compat = vehiclesInScope(vehicles || [], scopeKind, scopeId, pools).filter(hasDriver).length;
  const ready = valid && scopeChosen && compat > 0;

  const save = async () => {
    setSaving(true);
    try {
      const { scope_type, scope_id } = scopeToBackend({ kind: scopeKind, id: scopeId }, isAdmin, companies);
      const qs = `scope_type=${scope_type}` + (scope_id ? `&scope_id=${encodeURIComponent(scope_id)}` : "");
      await api.put(`/alert-settings/conducteur_non_identifie?${qs}`, { active: true, always_on: true, params: { driver_grace_s: Math.round(mn * 60) } });
      toast.success(editing ? "Alerte mise à jour" : "Alerte créée");
      onSaved(); onClose();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((typeof d === "string" ? d : d?.message || d?.error) || "Erreur");
    } finally { setSaving(false); }
  };

  return (
    <Shell title="Conducteur non identifié" icon={UserRound} color="#F5821F" onClose={onClose}
      footer={<button onClick={save} disabled={saving || !ready} data-testid="express-create" className="bg-[#17A2B8] hover:bg-[#1391a5] disabled:opacity-40 text-white font-bold px-6 py-2.5 rounded-md text-sm">{editing ? "Enregistrer" : "Créer l'alerte"}</button>}>
      <p className="text-base font-bold">M'alerter lorsqu'un véhicule est utilisé sans identification conducteur.</p>
      <div>
        <p className="label-mono mb-1.5">Délai avant alerte</p>
        <div className="flex items-end gap-3">
          <input type="number" min="1" max="60" value={minutes} autoFocus data-testid="express-grace" onChange={(e) => setMinutes(e.target.value)}
            className="bg-secondary/60 border-2 border-[#17A2B8]/40 focus:border-[#17A2B8] rounded-xl px-4 py-3 text-3xl font-extrabold text-center w-28 outline-none" />
          <span className="text-lg font-bold pb-3">minute(s)</span>
        </div>
      </div>
      <ScopeField {...{ scopeKind, setScopeKind, scopeId, setScopeId, companies, pools, vehicles, fleetAvailable }} disabled={!!editing} />
      <CompatNote vehicles={vehicles} pools={pools} scopeKind={scopeKind} scopeId={scopeId} pred={hasDriver} noun="équipés d'une identification conducteur" />
      <NotifyPlaceholder />
    </Shell>
  );
}

